import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import NoSelectionIcon from '@mui/icons-material/PersonOff';
import type { PopperProps } from '@mui/material/Popper';
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  ICON_SIZE,
  IconButton,
  Popover,
  Stack,
  TextField,
  Typography,
  UserAvatar,
  createFilterOptions,
} from '@pulse/ui';
import type { HTMLAttributes, ReactNode } from 'react';
import { useRef, useState } from 'react';

import { searchAdornment } from './SearchField';
import { LINE_CLAMP_2_SX, TruncatableText } from './TruncatableText';

// `Autocomplete`'s dropdown is normally its own independent floating `Popper`, portaled to
// `document.body` and positioned by measuring its anchor's on-screen rect. Nested inside a
// `Popover` (both `CompactEntityPicker` and the multi-select popover below), that produces two
// separate position computations for what's visually one panel — and they can genuinely diverge
// (observed live: the listbox rendered near the viewport's top-left while the search input
// correctly tracked the Popover, tens of pixels apart). Passing this as `PopperComponent`
// replaces Autocomplete's floating wrapper with a plain, non-portaled `div` that just flows
// normally right below the input — inside the Popover's own already-correctly-positioned box —
// so there's only one position computation (the Popover's) instead of two that can disagree.
// `Popper`'s own `children` can be a render-prop (for transition components) as well as plain
// nodes — Autocomplete only ever passes the latter, but both are handled here to satisfy
// `PopperProps`.
function InlineListboxWrapper({ disablePortal: _disablePortal, anchorEl: _anchorEl, open: _open, children, style, ...rest }: PopperProps) {
  const content = typeof children === 'function' ? children({ placement: 'bottom' }) : children;
  return (
    <div {...(rest as HTMLAttributes<HTMLDivElement>)} style={{ ...style, position: 'static', width: '100%' }}>
      {content}
    </div>
  );
}

export interface EntityOption {
  id: string;
  name: string;
  avatarUrl?: string;
}

// Each option row (and the "No X" clear row, same shape) is 36px for a typical short name —
// long names wrap instead of truncating (see renderEntityOption/renderEntityOptionCheckbox), so
// a row can grow taller than that. 200px comfortably shows 5-6 short-name rows at a glance while
// still hinting there's more to scroll, the same "partial next row" convention MUI's own Select
// menu uses — it just isn't a hard 5.5-row guarantee once wrapped rows are in the mix.
const LISTBOX_MAX_HEIGHT = 200;

// Deliberately typed over the `EntityOption` constraint, not a per-call generic `T` — passing a
// generic function reference as a prop value defeats the outer component's own `T` inference
// (Autocomplete's `renderOption` generic silently resolves to the constraint instead of unifying
// with the caller's `T`). A plain function over the constraint type is still assignable
// wherever a function over a narrower `T extends EntityOption` is expected (contravariant
// parameter position), so this works for every concrete entity type without being generic itself.
function renderEntityOption(
  props: HTMLAttributes<HTMLLIElement> & { key?: string },
  option: EntityOption,
  opts?: { hideAvatar?: boolean; noOptionClamp?: boolean },
) {
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {/* aria-hidden — otherwise the fallback initial's text node gets folded into the
            option's accessible name, breaking exact-name lookups against this option. */}
        {!opts?.hideAvatar && <UserAvatar aria-hidden="true" name={option.name} src={option.avatarUrl} size={UserAvatar.small} />}
        <TruncatableText
          text={option.name}
          variant="body2"
          placement="top"
          sx={{ flex: 1, minWidth: 0, ...(opts?.noOptionClamp ? { wordBreak: 'break-word' } : LINE_CLAMP_2_SX) }}
        />
      </Stack>
    </li>
  );
}

// Sentinel prepended to `SingleCompactEntityPicker`'s own `options` (see there) so "No X" is the
// listbox's real first row instead of a lookalike sitting above it. Namespaced so it can never
// collide with a real `EntityOption.id`, and kept purely internal to this file — a caller's
// `onChange` never sees it, that call site always translates it back to `null` first. Safe even
// though `T` may carry fields beyond `EntityOption` this placeholder doesn't populate, since
// nothing here ever reads past `id`/`name`/`avatarUrl` on it.
const CLEAR_OPTION_ID = '__clear__';

function isClearOption(option: EntityOption): boolean {
  return option.id === CLEAR_OPTION_ID;
}

// Single-select variant of the option row that also renders the CLEAR_OPTION_ID sentinel as a
// genuine listbox row — same avatar-badge + muted-label shape `ClearRow` renders for the
// multi-select popover below, but as a real option this one gets arrow-key nav, Home/End, and
// Enter-to-select for free, the same as any entity row, instead of needing its own click handler.
function renderEntityOrClearOption(
  props: HTMLAttributes<HTMLLIElement> & { key?: string },
  option: EntityOption,
  opts?: { hideAvatar?: boolean; noOptionClamp?: boolean },
) {
  if (!isClearOption(option)) return renderEntityOption(props, option, opts);
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Stack direction="row" alignItems="center" spacing={1}>
        {!opts?.hideAvatar && (
          <Avatar sx={{ width: 24, height: 24, bgcolor: 'action.selected', color: 'text.secondary' }}>
            <NoSelectionIcon sx={{ fontSize: ICON_SIZE.xxxs }} />
          </Avatar>
        )}
        <Typography variant="body2" color="text.secondary" noWrap>{option.name}</Typography>
      </Stack>
    </li>
  );
}

// Multi-select variant of the option row — same avatar+name, plus a checkbox reflecting the
// option's current (staged, not-yet-applied) selection state. `onChange` is a no-op: the row's
// own `li` click (spread via `optionProps`) is what actually toggles selection through
// Autocomplete; the checkbox is a visual reflection of that, not an independent control.
function renderEntityOptionCheckbox(
  props: HTMLAttributes<HTMLLIElement> & { key?: string },
  option: EntityOption,
  state: { selected: boolean },
) {
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Checkbox size="small" checked={state.selected} onChange={() => {}} sx={{ p: 0 }} />
        <UserAvatar aria-hidden="true" name={option.name} src={option.avatarUrl} size={UserAvatar.small} />
        <TruncatableText text={option.name} variant="body2" placement="top" sx={{ flex: 1, minWidth: 0, ...LINE_CLAMP_2_SX }} />
      </Stack>
    </li>
  );
}

// Multi-select counterpart to `renderEntityOrClearOption` — same CLEAR_OPTION_ID special-case,
// but picking it clears the entire staged draft rather than toggling a checkbox, so it's rendered
// without one (a checkbox implies "this specific row is/isn't selected", which doesn't apply to
// "deselect everything").
function renderEntityOrClearOptionCheckbox(
  props: HTMLAttributes<HTMLLIElement> & { key?: string },
  option: EntityOption,
  state: { selected: boolean },
) {
  if (!isClearOption(option)) return renderEntityOptionCheckbox(props, option, state);
  const { key, ...optionProps } = props;
  return (
    <li key={key} {...optionProps}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar sx={{ width: 24, height: 24, bgcolor: 'action.selected', color: 'text.secondary' }}>
          <NoSelectionIcon sx={{ fontSize: ICON_SIZE.xxxs }} />
        </Avatar>
        <Typography variant="body2" color="text.secondary" noWrap>{option.name}</Typography>
      </Stack>
    </li>
  );
}

// The shared multi-select popover used by both `EntityPicker`'s multi mode and
// `CompactEntityPicker`'s multi mode — search + checkbox list (via Autocomplete, so arrow-up/
// down + Enter keyboard nav come for free) with the same staged-draft/Cancel-Apply interaction
// as `MultiSelectPicker`: picks are held in local `draft` state and only committed to the
// parent's `onChange` when Apply (✓) is pressed. Cancel (✕) or clicking away discards `draft`
// and leaves `value` untouched. `trigger` is whatever the caller wants to render as the clickable
// anchor — a read-only chips field for `EntityPicker`, an arbitrary compact trigger (avatar
// stack, "+" button, ...) for `CompactEntityPicker`.
function MultiEntityPickerPopover<T extends EntityOption>({
  label,
  options,
  value,
  onChange,
  trigger,
  disabled,
  fullWidth,
}: {
  label: string;
  options: readonly T[];
  value: readonly T[];
  onChange: (value: T[]) => void;
  trigger: ReactNode;
  disabled?: boolean;
  fullWidth?: boolean;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<T[]>([...value]);
  const [search, setSearch] = useState('');

  function handleOpen() {
    setDraft([...value]);
    setSearch('');
    setOpen(true);
  }

  // "Select All" targets only the currently-filtered/visible options, same scoping as
  // MultiSelectPicker's own Select All — matches Autocomplete's own default `options` array
  // filtering, done manually here (rather than via `filterOptions`) so this same filtered list
  // can drive both the listbox and the Select All checkbox's target set/indeterminate state.
  const filtered = options.filter((o) => o.name.toLowerCase().includes(search.trim().toLowerCase()));
  const clearOption = { id: CLEAR_OPTION_ID, name: `No ${label}` } as T;
  const listOptions = [clearOption, ...filtered];
  const draftIds = new Set(draft.map((o) => o.id));
  const allVisibleSelected = filtered.length > 0 && filtered.every((o) => draftIds.has(o.id));
  const someVisibleSelected = filtered.some((o) => draftIds.has(o.id));

  function toggleSelectAll() {
    if (allVisibleSelected) {
      const filteredIds = new Set(filtered.map((o) => o.id));
      setDraft((prev) => prev.filter((o) => !filteredIds.has(o.id)));
    } else {
      setDraft((prev) => {
        const existingIds = new Set(prev.map((o) => o.id));
        return [...prev, ...filtered.filter((o) => !existingIds.has(o.id))];
      });
    }
  }

  return (
    <>
      {/* `Popover` is a sibling here, not a child of this `Box` — see the identical note on
          `CompactEntityPicker` below for why (portal event bubbling would otherwise re-open
          this the instant an option inside it is clicked). */}
      <Box
        ref={anchorRef}
        onClick={(e) => { if (disabled) return; e.stopPropagation(); handleOpen(); }}
        sx={{ display: 'inline-flex', cursor: disabled ? 'default' : 'pointer', width: fullWidth ? '100%' : undefined }}
      >
        {trigger}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        transitionDuration={0}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
      >
        {open && (
          <Box sx={{ width: 320, display: 'flex', flexDirection: 'column' }}>
            <Autocomplete
              open
              multiple
              disableCloseOnSelect
              disableClearable
              forcePopupIcon={false}
              PopperComponent={InlineListboxWrapper}
              // Caps the actual scrollport (the `<ul>` MUI renders inside the Popper) rather
              // than `InlineListboxWrapper` itself — Autocomplete gives that `<ul>` its own
              // independent max-height/overflow by default, so capping the wrapper too just
              // produced two nested, independently-scrolling containers (a visible double
              // scrollbar) instead of one.
              slotProps={{ listbox: { style: { maxHeight: LISTBOX_MAX_HEIGHT, overflowY: 'auto' } } }}
              options={listOptions}
              // Already filtered above (against `search`) so the same list backs both the
              // listbox and the Select All checkbox — Autocomplete's own default substring
              // filtering would just redo the same work.
              filterOptions={(x) => x}
              inputValue={search}
              onInputChange={(_, next, reason) => {
                // Multi-select Autocomplete resets its inputValue to '' after every pick
                // (reason: 'reset') — ignoring that keeps the search text (and filtered list)
                // in place across picks, so checking several matching options in a row doesn't
                // require re-typing the filter each time.
                if (reason === 'input') setSearch(next);
              }}
              noOptionsText={`No matching ${label.toLowerCase()}`}
              getOptionLabel={(o: T) => o.name}
              getOptionKey={(o: T) => o.id}
              isOptionEqualToValue={(a: T, b: T) => a.id === b.id}
              value={draft}
              onChange={(_, next) => {
                if (next.some(isClearOption)) { setDraft([]); return; }
                setDraft(next);
              }}
              renderTags={() => null}
              renderOption={renderEntityOrClearOptionCheckbox}
              // `renderInput`'s returned content is placed immediately before the listbox (which
              // `PopperComponent` renders right after it) — bundling Select All in here, below
              // the search TextField, is what puts it between the search box and the options
              // list rather than below the whole list, matching MultiSelectPicker's own layout.
              renderInput={(params) => (
                <Box>
                  <TextField
                    {...params}
                    autoFocus
                    inputRef={searchInputRef}
                    fullWidth
                    size="small"
                    placeholder={`Search ${label.toLowerCase()}…`}
                    onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: searchAdornment(),
                    }}
                    sx={{ p: 1.5, pb: 0.5, '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                  />
                  <FormControlLabel
                    sx={{ px: 1.5, ml: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={allVisibleSelected}
                        indeterminate={!allVisibleSelected && someVisibleSelected}
                        onChange={toggleSelectAll}
                      />
                    }
                    label="Select All"
                  />
                  <Divider />
                </Box>
              )}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ p: 1, pt: 0.5 }}>
              <IconButton aria-label="Cancel" onClick={() => setOpen(false)}>
                <CloseIcon fontSize={ICON_SIZE.sm} />
              </IconButton>
              <IconButton color="primary" aria-label="Apply" onClick={() => { onChange(draft); setOpen(false); }}>
                <CheckIcon fontSize={ICON_SIZE.sm} />
              </IconButton>
            </Stack>
          </Box>
        )}
      </Popover>
    </>
  );
}

interface EntityPickerCommon<T extends EntityOption> {
  label: string;
  options: readonly T[];
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  /** Matches the surrounding form's Select/TextField size. Defaults to 'small', matching the
   *  app theme's global MuiTextField/MuiFormControl/MuiSelect default — only pass 'medium' if
   *  the surrounding field actually opts out of that default too. */
  size?: 'small' | 'medium';
  /** Shown when nothing is selected, e.g. "Unassigned" / "No owner". */
  placeholder?: string;
  /** Optional icon prepended to the visible field label (not the search placeholder, which
   *  still derives from `label` alone). Opt-in — omit for the existing plain-text label. */
  labelIcon?: ReactNode;
  /** See `SingleCompactEntityPickerProps.hideAvatar` — same opt-in for a `EntityPicker` reused
   *  over a non-person `T` (single-select mode only; ignored in multi mode). */
  hideAvatar?: boolean;
  /** See `SingleCompactEntityPickerProps.noOptionClamp` (single-select mode only). */
  noOptionClamp?: boolean;
}

export interface SingleEntityPickerProps<T extends EntityOption> extends EntityPickerCommon<T> {
  multiple?: false;
  value: T | null;
  onChange: (value: T | null) => void;
}

export interface MultiEntityPickerProps<T extends EntityOption> extends EntityPickerCommon<T> {
  multiple: true;
  value: readonly T[];
  onChange: (value: T[]) => void;
}

export type EntityPickerProps<T extends EntityOption> = SingleEntityPickerProps<T> | MultiEntityPickerProps<T>;

// The shared "pick one or more entities" field — generic over any `{ id, name, avatarUrl? }`
// shape (people, but equally Module/Sprint/Change Request, ...) — replaces both the ad-hoc
// `Select` (no search) used for Owner/Lead/Head-style single fields and `MultiSelectPicker` (no
// keyboard nav) for Assignee-style multi fields. Single mode is a plain `Autocomplete`
// (search-as-you-type, arrow-up/down list navigation, Enter-to-select, all built in — no
// hand-rolled keyboard handling needed) that commits immediately, since there's only ever one
// pick to make. Multi mode instead opens `MultiEntityPickerPopover` — the same staged-draft/
// Cancel-Apply pattern as `MultiSelectPicker`, so picking several entities doesn't commit (and
// re-fetch/re-render) after every single click. See `CompactEntityPicker` below for the
// avatar-trigger variant used in tables/cards instead of a full form field.
//
// Declared via overloads (rather than one signature over the `SingleEntityPickerProps<T> |
// MultiEntityPickerProps<T>` union) so a JSX call site with `multiple` omitted/`multiple={true}`
// resolves to the right member and infers `T` from `options`/`value` directly, instead of
// TypeScript falling back to a same-shape union of both `onChange` signatures.
export function EntityPicker<T extends EntityOption>(props: SingleEntityPickerProps<T>): ReactNode;
export function EntityPicker<T extends EntityOption>(props: MultiEntityPickerProps<T>): ReactNode;
export function EntityPicker<T extends EntityOption>(props: EntityPickerProps<T>): ReactNode {
  // 'small', not 'medium' — the app theme (packages/ui/src/theme.ts) sets `size: 'small'` as the
  // *global* default for MuiTextField/MuiFormControl/MuiSelect, which every sibling Type/
  // Category/Priority/Due-Date field in these dialogs relies on implicitly (none of them pass
  // `size` either). Defaulting to 'medium' here silently opted this field out of that theme
  // default, rendering it 16px taller than every other field in the same row.
  const { label, options, required, disabled, fullWidth = true, size = 'small', placeholder, labelIcon, hideAvatar, noOptionClamp } = props;
  const visibleLabel = labelIcon ? <>{labelIcon}{label}</> : label;

  if (props.multiple) {
    const { value, onChange } = props;
    return (
      <MultiEntityPickerPopover
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled}
        fullWidth={fullWidth}
        trigger={
          <TextField
            label={visibleLabel}
            value=""
            placeholder={value.length === 0 ? placeholder : undefined}
            required={required}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            InputProps={{
              readOnly: true,
              startAdornment:
                value.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.75, pr: '14px', maxWidth: '100%' }}>
                    {value.map((o) => (
                      <Chip key={o.id} size="small" label={o.name} avatar={<UserAvatar name={o.name} src={o.avatarUrl} size={UserAvatar.small} />} />
                    ))}
                  </Stack>
                ) : undefined,
            }}
            sx={{
              '& .MuiInputBase-root': { flexWrap: 'wrap', cursor: disabled ? 'default' : 'pointer' },
              '& .MuiInputBase-input': { cursor: disabled ? 'default' : 'pointer', minWidth: 0, flex: value.length > 0 ? '0 0 0px' : 1 },
            }}
          />
        }
      />
    );
  }

  const { value, onChange } = props;
  // Matches each dialog's own original copy exactly: fields that passed a `placeholder`
  // (e.g. IssueFormDialog's Owner → "Unassigned") get that as the clear-row label; fields that
  // didn't (Project Manager, Linked User, Region/Department/BU Head, ...) fall back to "None" —
  // both were the exact `<em>...</em>` MenuItem text the old plain `Select` used. Required
  // fields (Risk/Escalation/Dependency Owner, Project Lead, ...) never had a blank option in
  // the original `Select` either, so the row is suppressed there too.
  return (
    <SingleCompactEntityPicker
      label={label}
      options={options}
      value={value}
      onChange={onChange}
      fullWidth={fullWidth}
      disabled={disabled}
      showClear={!required}
      clearLabel={placeholder ?? 'None'}
      hideAvatar={hideAvatar}
      noOptionClamp={noOptionClamp}
      trigger={
        <TextField
          label={visibleLabel}
          value={value?.name ?? ''}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          fullWidth={fullWidth}
          size={size}
          // `readOnly` (not `disabled`) — the field still needs to be focusable/clickable to
          // open the popover. The dropdown arrow is added back explicitly because this is now a
          // plain TextField playing the role of a combobox trigger, not a real Autocomplete/
          // Select — those render one automatically, and every sibling field in these dialogs
          // (Type, Priority, Module, Sprint, ...) is a `Select`, so omitting it made this one
          // field visually read as a different kind of control from the rest of the form.
          InputProps={{
            readOnly: true,
            endAdornment: <ArrowDropDownIcon sx={{ color: disabled ? 'action.disabled' : 'action.active' }} />,
          }}
          sx={{
            '& .MuiInputBase-root': { cursor: disabled ? 'default' : 'pointer' },
            '& .MuiInputBase-input': { cursor: disabled ? 'default' : 'pointer' },
          }}
        />
      }
    />
  );
}

interface CompactEntityPickerCommon<T extends EntityOption> {
  /** aria-label / search placeholder base, e.g. "owner" — not rendered as a visible label. */
  label: string;
  options: readonly T[];
  /** The clickable trigger (an avatar, an avatar stack, a "+" button, ...). */
  trigger: ReactNode;
}

export interface SingleCompactEntityPickerProps<T extends EntityOption> extends CompactEntityPickerCommon<T> {
  multiple?: false;
  value: T | null;
  onChange: (value: T | null) => void;
  /** Omits the avatar/fallback-initial badge from every option row (and the clear row's icon) —
   *  for a `CompactEntityPicker` reused over a non-person `T` (e.g. Module/Sprint), where an
   *  avatar-shaped badge would misleadingly read as a person. Off by default, so every existing
   *  entity-picker call site is unaffected. */
  hideAvatar?: boolean;
  /** Skips the option row's 2-line clamp, wrapping the full name instead — for a picker where
   *  names can run long enough that even 2 lines cuts them off (e.g. Module/Sprint). Off by
   *  default, matching every existing entity-picker call site's clamp-then-Tooltip behavior. */
  noOptionClamp?: boolean;
  fullWidth?: boolean;
}

export interface MultiCompactEntityPickerProps<T extends EntityOption> extends CompactEntityPickerCommon<T> {
  multiple: true;
  value: readonly T[];
  onChange: (value: T[]) => void;
}

export type CompactEntityPickerProps<T extends EntityOption> =
  | SingleCompactEntityPickerProps<T>
  | MultiCompactEntityPickerProps<T>;

// The inline/compact counterpart to `EntityPicker` — a click-to-open popover anchored to an
// arbitrary trigger element (an avatar, an avatar stack, ...) instead of a full-width form
// field. Replaces the `Menu`-of-`MenuItem`s pattern used by `InlineMenuCell` (ProjectPlannerTab)
// and `OwnerPicker`/`AssigneePicker` (ProjectBoardTab) for people specifically — those had no
// search and no keyboard navigation beyond MUI Menu's built-in type-ahead. Multi mode delegates
// entirely to `MultiEntityPickerPopover` (staged draft + Cancel/Apply, see there). Single mode's
// popover content is remounted fresh on every open (rather than kept alive with `open` toggled)
// so the internal Autocomplete's `autoFocus` reliably lands on the search input every time.
// Declared via overloads for the same reason as `EntityPicker` above.
export function CompactEntityPicker<T extends EntityOption>(props: SingleCompactEntityPickerProps<T>): ReactNode;
export function CompactEntityPicker<T extends EntityOption>(props: MultiCompactEntityPickerProps<T>): ReactNode;
export function CompactEntityPicker<T extends EntityOption>(props: CompactEntityPickerProps<T>): ReactNode {
  if (props.multiple) {
    return (
      <MultiEntityPickerPopover
        label={props.label}
        options={props.options}
        value={props.value}
        onChange={props.onChange}
        trigger={props.trigger}
      />
    );
  }

  return <SingleCompactEntityPicker {...props} />;
}

// Internal-only extension of the public single-compact props — `EntityPicker`'s single mode
// (below) reuses this same proven Popover-anchored, forced-open-Autocomplete architecture
// (rather than a plain always-mounted Autocomplete + `PaperComponent`, which turned out
// unreliable: clicking a custom row injected via `PaperComponent` on a *controlled-open*
// Autocomplete raced with Autocomplete's own blur/close handling and could silently fail to
// commit the clear). `fullWidth`/`disabled` size the trigger `Box` for a form-field context;
// `showClear`/`clearLabel` let a required field suppress the "No X" row entirely, and let
// `EntityPicker` reuse each dialog's original copy ("Unassigned", "None", ...) instead of the
// compact pickers' generic "No {label}".
function SingleCompactEntityPicker<T extends EntityOption>({
  label,
  options,
  value,
  onChange,
  trigger,
  fullWidth,
  disabled,
  showClear = true,
  clearLabel,
  hideAvatar,
  noOptionClamp,
}: SingleCompactEntityPickerProps<T> & { fullWidth?: boolean; disabled?: boolean; showClear?: boolean; clearLabel?: string }) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const filterOptions = createFilterOptions<T>({ limit: 50 });
  const resolvedClearLabel = clearLabel ?? `No ${label}`;
  // Prepended to `options` so "No X" is the listbox's own first row (arrow-key nav, Home/End,
  // and Enter-to-select all apply to it for free, same as any option) rather than a lookalike
  // row sitting above the listbox with its own click handler. See CLEAR_OPTION_ID above.
  const clearOption = { id: CLEAR_OPTION_ID, name: resolvedClearLabel } as T;
  const autocompleteOptions = showClear ? [clearOption, ...options] : options;

  return (
    <>
      {/* `Popover` is a sibling here, not a child of this `Box` — its content renders through a
          portal, but React still bubbles its synthetic click events up the *component* tree
          (portals don't affect that). Nesting it inside the `Box` below meant every option click
          also bubbled into this `Box`'s own `onClick`, re-opening the picker immediately after a
          selection closed it. */}
      <Box
        ref={anchorRef}
        onClick={(e) => { if (disabled) return; e.stopPropagation(); setInputValue(''); setOpen(true); }}
        sx={{ display: 'inline-flex', cursor: disabled ? 'default' : 'pointer', width: fullWidth ? '100%' : undefined }}
      >
        {trigger}
      </Box>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onClick={(e) => e.stopPropagation()}
        // The nested Autocomplete below owns a second, independent floating listbox (its own
        // Popper), which measures this Popover's position once on mount and never again — CSS
        // transforms (Popover's grow-in transition) don't fire the resize/scroll events Popper
        // listens for. Without this, the listbox can capture a stale mid-transition position and
        // render there permanently while the search input (plain document flow) ends up
        // correctly at the Popover's final resting place, visibly detached from each other.
        // Skipping the transition means the Popover is already at its final position by the time
        // the listbox's Popper measures it.
        transitionDuration={0}
        TransitionProps={{ onEntered: () => searchInputRef.current?.focus() }}
      >
        {open && (
          // Sized wrapping Box (not `sx={{ width }}` on the Autocomplete itself) — same structure
          // as MultiEntityPickerPopover, so the search TextField's `fullWidth` below resolves
          // against this Box's width the same way in both pickers.
          <Box sx={{ width: 280, display: 'flex', flexDirection: 'column' }}>
            <Autocomplete
              open
              forcePopupIcon={false}
              PopperComponent={InlineListboxWrapper}
              // See the identical note in MultiEntityPickerPopover — caps the actual `<ul>`
              // scrollport, not the outer Popper wrapper, to avoid a nested double scrollbar.
              slotProps={{ listbox: { style: { maxHeight: LISTBOX_MAX_HEIGHT, overflowY: 'auto' } } }}
              sx={{ '& .MuiAutocomplete-clearIndicator': { display: 'none' } }}
              options={autocompleteOptions}
              filterOptions={(_opts, state) => {
                const filtered = filterOptions([...options], state);
                return showClear ? [clearOption, ...filtered] : filtered;
              }}
              noOptionsText={`No matching ${label.toLowerCase()}`}
              getOptionLabel={(o: T) => o.name}
              getOptionKey={(o: T) => o.id}
              isOptionEqualToValue={(a: T, b: T) => a.id === b.id}
              value={value}
              inputValue={inputValue}
              onInputChange={(_, next, reason) => {
                if (reason === 'input') setInputValue(next);
              }}
              onChange={(_, next, reason) => {
                if (reason === 'clear') return;
                onChange(next && isClearOption(next) ? null : next);
                if (reason === 'selectOption') setOpen(false);
              }}
              renderOption={(optionProps, option) => renderEntityOrClearOption(optionProps, option, { hideAvatar, noOptionClamp })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  inputRef={searchInputRef}
                  fullWidth
                  size="small"
                  placeholder={`Search ${label.toLowerCase()}…`}
                  // See the identical note in MultiEntityPickerPopover — Autocomplete's own
                  // Escape handling swallows the key before this Popover's default
                  // Escape-closes-Modal behavior ever sees it, so it's closed directly here.
                  onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: searchAdornment(),
                  }}
                  sx={{ px: 1.5, pt: 1.5, '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                />
              )}
            />
            <Stack direction="row" justifyContent="flex-end" spacing={0.5} sx={{ p: 1, pt: 0.5 }}>
              <IconButton aria-label="Cancel" onClick={() => setOpen(false)}>
                <CloseIcon fontSize={ICON_SIZE.sm} />
              </IconButton>
            </Stack>
          </Box>
        )}
      </Popover>
    </>
  );
}
